import React, { useCallback, useMemo } from 'react';
import { styles, SdirButton, SdiWarningCircle } from '@sdir/sds';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import { generatePath } from 'react-router-dom';
import { GetApplicationsQueryResult } from '@sdir/httpclient/lib/clients/aps/application';
import { SimplifiedApplicationStatus } from '../../../types';
import { Card } from '../../Atoms/Card/Card';
import { routes } from '../../../constants/routes';
import { getSimplifiedApplicationStatus } from '../../../helpers/applicationHelpers';
import { CardRow, CertificateStatusField } from '../../Atoms';
import { convertApplicationStatus } from '../../../helpers/Certificates';
import { DisplayDateFormat, DisplayLongDateFormat } from '../../../helpers';
import { ToggleContent } from '../../Atoms/ToggleContent';
import { ToggleHideLetter } from '../../../constants/permissions';
import { useNavigateWithLang } from '../../../services/hooks/useNavigateWithLang';

interface ApplicationSummaryCardProps {
  application: GetApplicationsQueryResult;
  successfulPayment?: boolean;
}

export const ApplicationSummaryCard: React.FC<ApplicationSummaryCardProps> = ({
  application,
  successfulPayment,
}) => {
  const intl = useIntl();
  const applicationStatus = useMemo(() => {
    if (successfulPayment) {
      return SimplifiedApplicationStatus.Processing;
    }
    return getSimplifiedApplicationStatus(application);
  }, [application, successfulPayment]);

  const { cardBorder, cardBackground, showAlert } = useMemo(() => {
    switch (applicationStatus) {
      case SimplifiedApplicationStatus.Setup:
        return {
          cardBorder: styles.colors.alert.infoDark,
          cardBackground: styles.colors.alert.infoLight,
          showAlert: false,
        };
      case SimplifiedApplicationStatus.MoreInfoRequested:
      case SimplifiedApplicationStatus.AwaitingPayment:
        return {
          cardBorder: styles.colors.alert.warningDark,
          cardBackground: styles.colors.alert.warningLight,
          showAlert: true,
        };
      case SimplifiedApplicationStatus.Processing:
        return {
          cardBorder: styles.colors.alert.warningDark,
          cardBackground: styles.colors.alert.warningLight,
          showAlert: false,
        };
      case SimplifiedApplicationStatus.Approved:
        return {
          cardBorder: styles.colors.alert.successDark,
          cardBackground: styles.colors.alert.successLight,
          showAlert: false,
        };
      case SimplifiedApplicationStatus.Rejected:
        return {
          cardBorder: styles.colors.alert.errorDark,
          cardBackground: styles.colors.alert.errorLight,
          showAlert: false,
        };
      default:
        return {
          cardBorder: styles.colors.alert.infoDark,
          cardBackground: styles.colors.alert.infoLight,
          showAlert: false,
        };
    }
  }, [applicationStatus]);

  const dateLabel = intl.formatMessage({
    id:
      applicationStatus === SimplifiedApplicationStatus.Setup
        ? 'application.tile.application.date.created'
        : 'application.tile.application.date.submission',
  });

  return (
    <>
      <CardWrapper backgroundColor={cardBackground} borderColor={cardBorder}>
        {showAlert && <SdiWarningCircle color={styles.colors.alert.errorDark} height="24px" />}
        <H2>{intl.formatMessage({ id: 'application.status' })}</H2>
        <CardRow label={dateLabel}>{DisplayLongDateFormat(application.applicationDate)}</CardRow>
        {applicationStatus === SimplifiedApplicationStatus.MoreInfoRequested && (
          <CardRow label={intl.formatMessage({ id: 'application.tile.application.date.timeout' })}>
            {application?.timeoutDate ? DisplayDateFormat(application?.timeoutDate, true) : '–'}
          </CardRow>
        )}
        <CardRow label={intl.formatMessage({ id: 'courseandeducation.table.subheader3' })}>
          <CertificateStatusField
            certificateStatus={convertApplicationStatus(applicationStatus)}
            certificateStatusName={intl.formatMessage({
              id: `application.tile.status.${applicationStatus}`,
            })}
          />
          <ToggleContent toggleName={ToggleHideLetter} showLoader inverse>
            <LetterTextWrapper>
              {intl.formatMessage({ id: 'application.tile.application.lettertext' })}
            </LetterTextWrapper>
          </ToggleContent>
        </CardRow>
      </CardWrapper>
      <Buttons applicationUid={application.uid || ''} status={applicationStatus} />
    </>
  );
};

const Buttons: React.FC<{
  applicationUid: string;
  status: SimplifiedApplicationStatus;
}> = ({ applicationUid, status }) => {
  const navigate = useNavigateWithLang();
  const intl = useIntl();

  const canOpen = [SimplifiedApplicationStatus.Setup].includes(status);

  const openApplication = useCallback(() => {
    if (!canOpen) return;
    navigate(generatePath(routes.application.qualification, { applicationUid }));
  }, [applicationUid, canOpen, navigate]);

  if (!canOpen) return null;

  return (
    <ButtonsSection>
      {canOpen && (
        <SdirButton
          onClick={openApplication}
          type="primary"
          text={intl.formatMessage({ id: 'general.button.open.application' })}
          htmlType="button"
        />
      )}
    </ButtonsSection>
  );
};

const LetterTextWrapper = styled.div`
  display: flex;
  font-size: 2rem;
  margin-top: 20px;
  color: ${styles.colors.alert.errorDark};
`;

const ButtonsSection = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 48px 0;

  & > button:last-child {
    margin-left: auto;
  }
`;

const CardWrapper = styled(Card)<{
  backgroundColor: string;
  borderColor: string;
}>`
  background-color: ${({ backgroundColor }) => backgroundColor};
  border: 1px solid ${({ borderColor }) => borderColor};
  margin-bottom: 24px;
  position: relative;

  > .card__contents {
    > svg {
      position: absolute;
      top: -12px;
      right: -12px;
      background: ${styles.colors.background.white};
      border-radius: 50%;
    }

    > :last-child {
      margin-bottom: 0;
    }
  }
`;

const H2 = styled.h2`
  margin: 0 0 8px 0;
  font-size: 24px;
  margin-bottom: 32px;
`;
